import { ApolloClient, InMemoryCache, from, ApolloLink } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { createApolloProvider } from '@vue/apollo-option'
import { createUploadLink } from 'apollo-upload-client'
import { getProcessinguuid, getFingerprint } from '@/helpers/auth'

// Auth middleware usando setContext para garantir que headers sejam propagados corretamente
const authMiddlewareLink = setContext((_, { headers = {} }) => {
  return {
    headers: {
      ...headers,
      processinguuid: getProcessinguuid(),
      fingerprint: getFingerprint(),
    },
  }
})

export const isExpiredError = (e) => {
  return ['session.expired', 'authentication.error.expired.jwt.token', 'Context creation failed: authentication.error.expired.jwt.token'].includes(e.message)
}

export const isRefreshExpiredError = (e) => {
  return ['session.refresh.expired'].includes(e.message)
}

const errorLink = onError(({ graphQLErrors }) => {
  console.log('GQL ERROR - %cError', 'background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;', graphQLErrors)
  if (graphQLErrors && graphQLErrors.some((err) => err.message.startsWith('authentication.error'))) {
    window.location.replace('/#/expired-session')
  }
  if (graphQLErrors && graphQLErrors.some((err) => err.message.startsWith('invalid.session'))) {
    window.location.replace('/#/expired-session')
  }
  if (graphQLErrors && graphQLErrors.some((err) => isExpiredError(err))) {
    window.location.replace('/#/expired-session')
  }
  if (graphQLErrors && graphQLErrors.some((err) => isRefreshExpiredError(err))) {
    window.location.replace('/#/expired-session')
  }
})

// Link base para uploads
const baseUploadLink = createUploadLink({
  uri: process.env.VUE_APP_FED_GATEWAY,
  fetchOptions: {
    credentials: 'include',
  },
})

// Link customizado que suporta URIs dinâmicas do context
const dynamicUploadLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext()
  const { uri, headers: contextHeaders = {} } = context

  // Se houver uma URI customizada no context, criar um novo link de upload para ela
  if (uri && uri !== process.env.VUE_APP_FED_GATEWAY) {
    const customUploadLink = createUploadLink({
      uri: uri,
      fetchOptions: {
        credentials: 'include',
      },
    })

    // Mesclar headers: primeiro os headers existentes, depois os do context (para que contextHeaders sobrescrevam se necessário)
    const currentHeaders = context.headers || {}
    operation.setContext({
      ...context,
      headers: {
        ...currentHeaders,
        ...contextHeaders, // Headers do context têm prioridade
      },
    })

    return customUploadLink.request(operation, forward)
  }

  // Caso contrário, usar o link base
  return baseUploadLink.request(operation, forward)
})

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    // Desabilitar normalização para objetos de métricas que compartilham o mesmo id
    // Isso evita que metricsByQuestions e metricsByUnits compartilhem referências
    dataIdFromObject: (object) => {
      if (
        object.__typename &&
        (object.__typename.includes('Metrics') || object.__typename.includes('ChoiceMetrics') || object.__typename.includes('RatingMetrics'))
      ) {
        return null
      }
      return object.id || object._id || null
    },
  }),
  link: from([authMiddlewareLink, errorLink, dynamicUploadLink]),
})

const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
})

export default apolloProvider
